import type { FC } from "react";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { MarkdownWrapper } from "@ui/components/MarkdownWrapper";
import { useUser } from "@ui/hooks/useUser";
import { replaceOneTrustPlaceholdersWithLinks } from "@ui/utils/linkReplacer/linkReplacer";
import { stripHtmlTags } from "@ui/utils/stripHtmlTags/stripHtmlTags";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { Checkbox } from "@jti/core-components/components/Checkbox";
import type { Steps } from "@jti/fe-common";
import { CasPurposesApi } from "@jti/rest-api";

import checkboxStyles from "./ConsentCheckbox.module.scss";

const { sanitize } = dompurify;

type ConsentCheckboxProps = {
  displayConsent?: "always" | "onlyNotLoggedIn" | "onlyLoggedIn";
  dataTestId?: string;
  consent?: Steps;
  namePrefix?: string;
  showRequiredAsterisk?: boolean;
};

type ConsentCheckboxElement = {
  id: string;
  name: string;
  description: string;
  mandatory: boolean;
  default: boolean;
};

type ConsentCheckboxValue = {
  checked?: boolean;
};

export const ConsentCheckbox: FC<ConsentCheckboxProps> = ({
  displayConsent,
  dataTestId = "formConsentCheckbox",
  consent = "contact_form",
  namePrefix = "",
  showRequiredAsterisk = false,
}) => {
  const config = useConfig();
  const [checkboxes, setCheckboxes] = useState<ConsentCheckboxElement[]>([]);
  const { isUserLoggedIn } = useUser();
  const { setValue } = useFormContext();

  const isConsentCheckboxHidden =
    (displayConsent === "onlyLoggedIn" && !isUserLoggedIn) || (displayConsent === "onlyNotLoggedIn" && isUserLoggedIn);
  useEffect(() => {
    if (!isConsentCheckboxHidden) {
      CasPurposesApi.init()
        .getPurposesByStep(consent)
        .then((response) => {
          const sanitizedResponse = response.map((element): ConsentCheckboxElement => {
            const name = namePrefix ? [namePrefix, element.id].join(".") : element.id;
            setValue(`${name}.language`, element.language);
            return {
              id: element.id,
              name,
              description: element.description,
              mandatory: element.mandatory,
              default: element.default,
            };
          });
          setCheckboxes(sanitizedResponse);
        });
    }
  }, [isConsentCheckboxHidden, consent, namePrefix, setValue]);

  if (isConsentCheckboxHidden) {
    return null;
  }

  return (
    <div className={checkboxStyles.container}>
      {checkboxes.map((element, key) => {
        const sanitizedDescription = sanitize(
          config.ONE_TRUST_DYNAMIC_LINKS
            ? replaceOneTrustPlaceholdersWithLinks(element.description, config.ONE_TRUST_DYNAMIC_LINKS ?? [])
            : element.description,
          { ADD_ATTR: ["target"] }
        );

        return (
          <Controller
            key={key}
            name={element.name}
            defaultValue={{
              checked: element.default,
              id: element.id,
              name: element.name,
              description: element.description,
              mandatory: element.mandatory,
              default: element.default,
            }}
            rules={{
              validate: (value?: ConsentCheckboxValue) => (element.mandatory ? !!value?.checked : true),
            }}
            render={({ field, fieldState: { error } }) => (
              <div className={checkboxStyles.checkboxContainer}>
                <Checkbox
                  aria-label={stripHtmlTags(sanitizedDescription)}
                  checked={field.value?.checked ?? false}
                  name={field.name}
                  label={
                    <div className={checkboxStyles.checkboxLabel}>
                      {showRequiredAsterisk && element.mandatory && <span aria-hidden="true"> *</span>}
                      <MarkdownWrapper>{sanitizedDescription}</MarkdownWrapper>
                    </div>
                  }
                  onBlur={field.onBlur}
                  required={element.mandatory}
                  id={element.id}
                  ref={field.ref}
                  onCheckedChange={(checked) => {
                    field.onChange({
                      ...field.value,
                      checked,
                    });
                  }}
                  status={error ? "error" : "default"}
                  errorMessage={error?.message}
                  data-testid={dataTestId}
                />
              </div>
            )}
          />
        );
      })}
    </div>
  );
};
